import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Pagination,
} from 'react-bootstrap';
import { default as Selector } from 'react-select';
import { toast } from 'react-toastify';
import { FaSearch, FaFileExcel } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { customStyles } from '~/Styles/reactSelectStyles';
import { exportarEmExcel, textoExcel } from '~/Utils/Helpers';
import {
  consultaImoveisBuscarEmpreendimentos,
  listarCobradoresRefinanciamentosInadimplencia,
  listarRefinanciamentosInadimplencia,
} from '~/Services/Service';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';

// W-0805 — Imob > Cobrança > Re-Financiamentos em Inadimplência. Read-only.
// Fiel a ModuloImob/URefInad.pas: período obrigatório + combos (cobrador/empreendimento)
// + Tipo de Título / Foco / Opção (analítico|sintético) + grid agrupado + export.
// RN-14: sem característica (acesso a qualquer interno); o gate de menu/rota é o char 630
// (MigracaoERP — portão de migração HML, ortogonal a RBAC). sojuris/pezao já são aplicados
// no servidor sobre o combo de cobrador. Modo sintético é agregação de render no front.

const TIPO_TITULO_OPCOES = [
  { valor: 0, rotulo: 'Todos' },
  { valor: 1, rotulo: 'Normal' },
  { valor: 2, rotulo: 'Int.Vendas' },
];

const FOCO_OPCOES = [
  { valor: 0, rotulo: 'Cobradores' },
  { valor: 1, rotulo: 'Empreendimento' },
];

const MODO_OPCOES = [
  { valor: 0, rotulo: 'Analítico' },
  { valor: 1, rotulo: 'Sintético' },
];

// Colunas do modo analítico (RN-9) — mapeadas aos aliases reais do backend (snake_case).
const COLUNAS = [
  { campo: 'cobrador', titulo: 'Cobrador', tipo: 'texto' },
  { campo: 'nome_da_familia', titulo: 'Empreendimento', tipo: 'texto' },
  { campo: 'mapa1', titulo: 'Qd', tipo: 'texto' },
  { campo: 'mapa2', titulo: 'Lt', tipo: 'texto' },
  { campo: 'cliente', titulo: 'Cliente', tipo: 'texto' },
  { campo: 'boleto', titulo: 'Boleto', tipo: 'texto' },
  { campo: 'acordo', titulo: 'Dt.Acordo', tipo: 'data' },
  { campo: 'data_inat', titulo: 'Dt.Inativação', tipo: 'data' },
];

const ITENS_POR_PAGINA = 20;

const formatarData = valor => {
  if (!valor) return '';
  const d = new Date(valor);
  if (isNaN(d.getTime())) return '';
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const ano = d.getFullYear();
  return `${dia}/${mes}/${ano}`;
};

// Cliente = conta_cedente + apelido_conta (RN-9).
const rotuloCliente = item => {
  const apelido = item.apelido_conta ? item.apelido_conta : '';
  return apelido ? `${item.conta_cedente} - ${apelido}` : `${item.conta_cedente}`;
};

const valorCelula = (item, coluna) => {
  if (coluna.campo === 'cliente') return rotuloCliente(item);
  if (coluna.tipo === 'data') return formatarData(item[coluna.campo]);
  return item[coluna.campo];
};

// Igual a valorCelula, mas envolve as datas em textoExcel para que o Excel as mantenha
// como texto (evita conversão automática que gera `######` e troca dd/mm conforme locale).
const valorCelulaExport = (item, coluna) => {
  if (coluna.tipo === 'data') return textoExcel(formatarData(item[coluna.campo]));
  return valorCelula(item, coluna);
};

const RefinanciamentosInadimplencia = () => {
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');
  const [cobradoresOptions, setCobradoresOptions] = useState([]);
  const [cobradorSelecionado, setCobradorSelecionado] = useState(null);
  const [empreendimentosOptions, setEmpreendimentosOptions] = useState([]);
  const [empreendimentoSelecionado, setEmpreendimentoSelecionado] = useState(
    null
  );
  const [tipoTitulo, setTipoTitulo] = useState(0);
  const [foco, setFoco] = useState(0);
  const [modo, setModo] = useState(0);

  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [jaGerou, setJaGerou] = useState(false);
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    carregarCobradores();
    carregarEmpreendimentos();
  }, []);

  const carregarCobradores = async () => {
    try {
      const res = await listarCobradoresRefinanciamentosInadimplencia();
      const lista = res ? res : [];
      setCobradoresOptions(
        lista.map(c => ({
          value: c.codigo_conta,
          label: `${c.codigo_conta} - ${c.nome_prestador}`,
        }))
      );
    } catch (err) {
      toast.error(
        err && err.message ? err.message : 'Erro ao carregar cobradores.'
      );
    }
  };

  const carregarEmpreendimentos = async () => {
    try {
      const res = await consultaImoveisBuscarEmpreendimentos();
      const lista = res ? res : [];
      setEmpreendimentosOptions(
        lista.map(e => ({
          value: e.codigo_familia,
          label: `${e.codigo_familia} - ${e.nome_da_familia}`,
        }))
      );
    } catch (err) {
      toast.error(
        err && err.message ? err.message : 'Erro ao carregar empreendimentos.'
      );
    }
  };

  // Datas obrigatórias antes de habilitar Gerar (RN-1/RN-2).
  const periodoValido = dataInicial !== '' && dataFinal !== '';

  const gerar = async () => {
    if (!periodoValido) {
      toast.warning('Informe a Data Inicial e a Data Final.');
      return;
    }
    try {
      setCarregando(true);
      const filtros = {
        dataInicial,
        dataFinal,
        codigoCobrador: cobradorSelecionado ? cobradorSelecionado.value : null,
        codigoFamilia: empreendimentoSelecionado
          ? empreendimentoSelecionado.value
          : null,
        tipoTitulo,
        foco,
        modo,
      };
      const res = await listarRefinanciamentosInadimplencia(filtros);
      setRegistros(res ? res : []);
      setJaGerou(true);
      setPagina(1);
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao gerar relatório.');
    } finally {
      setCarregando(false);
    }
  };

  // Sintético (RN-10): oculta o detalhe e mostra a contagem por quebra. A quebra
  // segue o Foco — Cobradores (0) agrupa por cobrador; Empreendimento (1) por família.
  const resumoSintetico = useMemo(() => {
    if (modo !== 1) return [];
    const mapa = new Map();
    registros.forEach(item => {
      const chave =
        foco === 1
          ? item.nome_da_familia
            ? item.nome_da_familia
            : '(Sem empreendimento)'
          : item.cobrador != null
          ? String(item.cobrador)
          : '(Sem cobrador)';
      const atual = mapa.has(chave) ? mapa.get(chave) : 0;
      mapa.set(chave, atual + 1);
    });
    const linhas = [];
    mapa.forEach((quantidade, chave) => {
      linhas.push({ chave, quantidade });
    });
    return linhas;
  }, [modo, foco, registros]);

  const totalRegistros = modo === 1 ? resumoSintetico.length : registros.length;
  const totalPaginas = Math.ceil(totalRegistros / ITENS_POR_PAGINA) || 1;

  const registrosPaginados = registros.slice(
    (pagina - 1) * ITENS_POR_PAGINA,
    pagina * ITENS_POR_PAGINA
  );
  const resumoPaginado = resumoSintetico.slice(
    (pagina - 1) * ITENS_POR_PAGINA,
    pagina * ITENS_POR_PAGINA
  );

  const exportarExcel = () => {
    if (totalRegistros === 0) return;
    if (modo === 1) {
      const rotuloQuebra = foco === 1 ? 'Empreendimento' : 'Cobrador';
      const cabecalho = [rotuloQuebra, 'Quantidade'];
      const linhas = resumoSintetico.map(r => [r.chave, r.quantidade]);
      exportarEmExcel(
        [cabecalho, ...linhas],
        'refinanciamentos-inadimplencia-sintetico.csv'
      );
      return;
    }
    const cabecalho = COLUNAS.map(c => c.titulo);
    const linhas = registros.map(item =>
      COLUNAS.map(c => valorCelulaExport(item, c))
    );
    exportarEmExcel(
      [cabecalho, ...linhas],
      'refinanciamentos-inadimplencia.csv'
    );
  };

  const renderizarPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const itens = [];
    const maxBotoes = 5;

    itens.push(
      <Pagination.First
        key="first"
        onClick={() => setPagina(1)}
        disabled={pagina === 1}
      />
    );
    itens.push(
      <Pagination.Prev
        key="prev"
        onClick={() => setPagina(pagina - 1)}
        disabled={pagina === 1}
      />
    );

    let inicioPaginas = Math.max(1, pagina - Math.floor(maxBotoes / 2));
    const fimPaginas = Math.min(totalPaginas, inicioPaginas + maxBotoes - 1);
    if (fimPaginas - inicioPaginas < maxBotoes - 1) {
      inicioPaginas = Math.max(1, fimPaginas - maxBotoes + 1);
    }

    if (inicioPaginas > 1) {
      itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }

    for (let i = inicioPaginas; i <= fimPaginas; i += 1) {
      itens.push(
        <Pagination.Item
          key={i}
          active={i === pagina}
          onClick={() => setPagina(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    if (fimPaginas < totalPaginas) {
      itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }

    itens.push(
      <Pagination.Next
        key="next"
        onClick={() => setPagina(pagina + 1)}
        disabled={pagina === totalPaginas}
      />
    );
    itens.push(
      <Pagination.Last
        key="last"
        onClick={() => setPagina(totalPaginas)}
        disabled={pagina === totalPaginas}
      />
    );

    return (
      <div
        className="d-flex justify-content-between align-items-center px-3 py-2"
        style={{ borderTop: '1px solid #dee2e6' }}
      >
        <small className="text-muted">
          {`Mostrando ${
            totalRegistros > 0 ? (pagina - 1) * ITENS_POR_PAGINA + 1 : 0
          } até ${Math.min(
            pagina * ITENS_POR_PAGINA,
            totalRegistros
          )} de ${totalRegistros} registros`}
        </small>
        <Pagination className="mb-0">{itens}</Pagination>
      </div>
    );
  };

  const rotuloQuebraSintetico = foco === 1 ? 'Empreendimento' : 'Cobrador';

  return (
    <div>
      <Header title="Re-Financiamentos em Inadimplência" />
      <Container fluid className="mt-3">
        <Card className="mb-3">
          <Card.Header>
            <strong>Filtros</strong>
          </Card.Header>
          <Card.Body>
            <Row className="align-items-end">
              <Col xs={12} md={3} className="mb-3">
                <Form.Label>Data Inicial *</Form.Label>
                <Form.Control
                  type="date"
                  value={dataInicial}
                  onChange={e => setDataInicial(e.target.value)}
                />
              </Col>
              <Col xs={12} md={3} className="mb-3">
                <Form.Label>Data Final *</Form.Label>
                <Form.Control
                  type="date"
                  value={dataFinal}
                  onChange={e => setDataFinal(e.target.value)}
                />
              </Col>
              <Col xs={12} md={3} className="mb-3">
                <Form.Label>Cobrador</Form.Label>
                <Selector
                  isClearable
                  styles={customStyles}
                  placeholder="Todos os cobradores..."
                  noOptionsMessage={() => 'Nenhum cobrador encontrado'}
                  options={cobradoresOptions}
                  value={cobradorSelecionado}
                  onChange={selecionado =>
                    setCobradorSelecionado(selecionado || null)
                  }
                />
              </Col>
              <Col xs={12} md={3} className="mb-3">
                <Form.Label>Empreendimento</Form.Label>
                <Selector
                  isClearable
                  styles={customStyles}
                  placeholder="Todos os empreendimentos..."
                  noOptionsMessage={() => 'Nenhum empreendimento encontrado'}
                  options={empreendimentosOptions}
                  value={empreendimentoSelecionado}
                  onChange={selecionado =>
                    setEmpreendimentoSelecionado(selecionado || null)
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={4} className="mb-3">
                <Form.Label className="d-block">Tipo de Título</Form.Label>
                {TIPO_TITULO_OPCOES.map(opcao => (
                  <Form.Check
                    inline
                    type="radio"
                    name="tipoTitulo"
                    id={`tipoTitulo-${opcao.valor}`}
                    key={opcao.valor}
                    label={opcao.rotulo}
                    checked={tipoTitulo === opcao.valor}
                    onChange={() => setTipoTitulo(opcao.valor)}
                  />
                ))}
              </Col>
              <Col xs={12} md={4} className="mb-3">
                <Form.Label className="d-block">Foco</Form.Label>
                {FOCO_OPCOES.map(opcao => (
                  <Form.Check
                    inline
                    type="radio"
                    name="foco"
                    id={`foco-${opcao.valor}`}
                    key={opcao.valor}
                    label={opcao.rotulo}
                    checked={foco === opcao.valor}
                    onChange={() => setFoco(opcao.valor)}
                  />
                ))}
              </Col>
              <Col xs={12} md={4} className="mb-3">
                <Form.Label className="d-block">Opção</Form.Label>
                {MODO_OPCOES.map(opcao => (
                  <Form.Check
                    inline
                    type="radio"
                    name="modo"
                    id={`modo-${opcao.valor}`}
                    key={opcao.valor}
                    label={opcao.rotulo}
                    checked={modo === opcao.valor}
                    onChange={() => setModo(opcao.valor)}
                  />
                ))}
              </Col>
            </Row>
            <Row>
              <Col xs={12} className="d-flex align-items-center flex-wrap">
                <Button
                  variant="primary"
                  className="mr-2"
                  disabled={!periodoValido || carregando}
                  onClick={gerar}
                >
                  <FaSearch className="mr-1" />
                  Gerar
                </Button>
                <Button
                  variant="outline-success"
                  disabled={carregando || totalRegistros === 0}
                  onClick={exportarExcel}
                >
                  <FaFileExcel className="mr-1" />
                  Exportar Excel
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {carregando && <Loading />}

        {!carregando && jaGerou && (
          <Card>
            <Card.Body className="table-responsive">
              {totalRegistros === 0 && (
                <div className="text-center text-muted py-3">
                  Nenhum registro encontrado
                </div>
              )}

              {totalRegistros > 0 && modo === 0 && (
                <>
                  <Table className="table table-striped table-bordered table-hover table-sm">
                    <Thead>
                      <Tr className="text-center">
                        {COLUNAS.map(c => (
                          <Th key={c.campo}>{c.titulo}</Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody className="text-center">
                      {registrosPaginados.map((item, indice) => (
                        <Tr
                          key={`${(pagina - 1) * ITENS_POR_PAGINA +
                            indice}-${item.conta_cedente}-${item.boleto}`}
                        >
                          {COLUNAS.map(c => (
                            <Td key={c.campo}>{valorCelula(item, c)}</Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  {renderizarPaginacao()}
                </>
              )}

              {totalRegistros > 0 && modo === 1 && (
                <>
                  <Table className="table table-striped table-bordered table-hover table-sm">
                    <Thead>
                      <Tr className="text-center">
                        <Th>{rotuloQuebraSintetico}</Th>
                        <Th>Quantidade</Th>
                      </Tr>
                    </Thead>
                    <Tbody className="text-center">
                      {resumoPaginado.map((linha, indice) => (
                        <Tr
                          key={`${(pagina - 1) * ITENS_POR_PAGINA +
                            indice}-${linha.chave}`}
                        >
                          <Td>{linha.chave}</Td>
                          <Td>{linha.quantidade}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  {renderizarPaginacao()}
                </>
              )}
            </Card.Body>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default RefinanciamentosInadimplencia;
